import { toast } from "react-toastify";
import {
  addPartner,
  createPartner,
  setNewPartnerPopoverOpen,
} from "../main.slice";
import { useAppDispatch } from "hooks/hooks";
import { ICreatePartnerRequestParams } from "Dashboard/scenes/Partners/domain/ICreatePartnerRequestParams";
import {
  Control,
  Controller,
  FieldErrorsImpl,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetFocus,
} from "react-hook-form";
import ActivitySelectCreate from "./ActivitySelectCreate";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import MunicipalitySelectCreate from "../../../components/MunicipalitySelectCreate";
import { ChangeEvent, FocusEvent, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import "../../../styles/main.scss";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesPartner from "../../../styles/popovers.module.scss";
import { IOption } from "Dashboard/domain/IOption";

interface IProps {
  handleSubmit: UseFormHandleSubmit<ICreatePartnerRequestParams>;
  register: UseFormRegister<ICreatePartnerRequestParams>;
  control: Control<ICreatePartnerRequestParams, any>;
  errors: Partial<FieldErrorsImpl<ICreatePartnerRequestParams>>;
  setFocus: UseFormSetFocus<ICreatePartnerRequestParams>;
  activityValue: IOption | undefined;
  setActivityValue: React.Dispatch<React.SetStateAction<IOption | undefined>>;
  municipalityValue: IOption | undefined;
  setMunicipalityValue: React.Dispatch<
    React.SetStateAction<IOption | undefined>
  >;
  bankAccount: string;
  setBankAccount: React.Dispatch<React.SetStateAction<string>>;
  bankAccountError: string | undefined;
  setBankAccountError: React.Dispatch<React.SetStateAction<string | undefined>>;
  resetForm: () => void;
}

const NewPartnerPopover = (props: IProps) => {
  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    register,
    control,
    errors,
    setFocus,
    bankAccount,
    bankAccountError,
    setBankAccount,
    setBankAccountError,
    resetForm,
  } = props;

  const onSubmit = async (data: ICreatePartnerRequestParams) => {
    if (bankAccountError) {
      toast.error("Neispravan broj racuna");
      return;
    }
    try {
      data.bankAccount = bankAccount;
      const partner = await dispatch(createPartner(data)).unwrap();
      if (partner) {
        dispatch(addPartner(partner));
        resetForm();
        toast.success("Novi saradnik je uspešno dodat.");
      }
      dispatch(setNewPartnerPopoverOpen(false));
    } catch (errResponse: any) {
      if (errResponse.status === 409) {
        const errorMessage: string = errResponse.data.message;
        const pattern = "other partner: ";
        const otherPartner = errorMessage.substring(errorMessage.indexOf(pattern) + pattern.length, errorMessage.length - 1);
        toast.error(`PIB je zauzet (${otherPartner})`);
      } else {
        toast.error("Doslo je do nepoznate greške");
      }
    }
  };

  useEffect(() => {
    setFocus("shortName");
  }, [setFocus]);

  const validateAndFormatBankAccount = (
    inputValue: string,
    format: boolean,
    showError: boolean
  ) => {
    if (!showError) {
      setBankAccountError(undefined);
    }
    if (inputValue === "") {
      setBankAccountError(undefined);
      setBankAccount(inputValue);
      return;
    }

    const cleanedInputValue = inputValue.replace(/[^\d]/g, "");
    const regexPattern = /^(\d{3})(\d{0,13})(\d{2})$/;
    const matches = cleanedInputValue.match(regexPattern);

    if (!matches) {
      setBankAccount(cleanedInputValue);
      if (showError) {
        setBankAccountError("Neispravan format broja računa");
      }
      return;
    }

    let bankCodeRaw = matches[1];
    let accountNumberRaw = matches[2];
    let controlNumberRaw = matches[3];

    if (
      bankCodeRaw === "" ||
      accountNumberRaw === "" ||
      controlNumberRaw === ""
    ) {
      setBankAccount(cleanedInputValue);
      if (showError) {
        setBankAccountError("Neispravan format broja računa");
      }
      return;
    }
    let bankCode = BigInt(bankCodeRaw);
    let accountNumber = BigInt(accountNumberRaw);
    let controlNumber = BigInt(controlNumberRaw);
    if (
      ((bankCode * BigInt(10000000000000) + accountNumber) * BigInt(100) +
        controlNumber) %
      BigInt(97) !==
      BigInt(1)
    ) {
      setBankAccount(cleanedInputValue);
      if (showError) {
        setBankAccountError("Neispravan kontrolni broj računa");
      }
      return;
    }
    const formattedValue = [
      bankCodeRaw,
      accountNumberRaw,
      controlNumberRaw,
    ].join(format ? "-" : "");

    setBankAccount(formattedValue);
    setBankAccountError(undefined);
  };

  const handleBankAccountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    validateAndFormatBankAccount(inputValue, false, false);
  };

  const handleBankAccountFocusIn = (
    event: FocusEvent<HTMLInputElement, Element>
  ) => {
    const inputValue = event.target.value;
    validateAndFormatBankAccount(inputValue, false, false);
  };

  const handleBankAccountFocusOut = (
    event: FocusEvent<HTMLInputElement, Element>
  ) => {
    const inputValue = event.target.value;
    validateAndFormatBankAccount(inputValue, true, true);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`${classesPartner.newPartnerPopover} ${classes.basicPopover}`}
    >
      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>ime saradnika</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.shortName ? classes.itemError : ""}`}
            {...register("shortName", { required: true })}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>ime za fakturu</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.fullName ? classes.itemError : ""}`}
            {...register("fullName", { required: true })}
          />
        </div>
      </div>

      <div className={`${classes.row}
            ${errors.activityId ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>delatnost</label>
          <Controller
            name="activityId"
            control={control}
            rules={{
              required: false,
            }}
            render={({ field: { onChange } }) => (
              <ActivitySelectCreate
                customStyles={styles.customStyleClassic}
                error={errors.activityId}
                onChange={onChange}
                value={props.activityValue}
                setValue={props.setActivityValue}
                preserveInput={true}
              />
            )}
          />
        </div>
      </div>

      <div className={`${classes.row}
            ${bankAccountError ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>br. računa</label>
          <input
            name="bankAccount"
            value={bankAccount}
            onChange={handleBankAccountChange}
            onFocus={handleBankAccountFocusIn}
            onBlur={handleBankAccountFocusOut}
            type="text"
            placeholder=""
            className={classes.itemBorder}
          />
        </div>
      </div>

      <div className={`${classes.row}
            ${errors.id ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>br.lk. / PIB</label>
          <input
            type="text"
            className={classes.itemBorder}
            {...register("id", { required: false })}
          />
        </div>
      </div>

      <div className={`${classes.row}
            ${errors.identificationNumber ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>matični broj</label>
          <input
            type="text"
            className={classes.itemBorder}
            {...register("identificationNumber", { required: false })}
          />
        </div>
      </div>

      <div className={`${classes.row}
            ${errors.municipalityId ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>sup / mesto</label>
          <Controller
            name="municipalityId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: false,
            }}
            render={({ field: { onChange } }) => (
              <MunicipalitySelectCreate
                customStyles={styles.customStyleClassic}
                error={errors.municipalityId}
                onChange={onChange}
                value={props.municipalityValue}
                setValue={props.setMunicipalityValue}
                preserveInput={true}
              />
            )}
          />
        </div>
      </div>

      <div className={`${classes.row}
            ${errors.phones ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>telefoni</label>
          <textarea rows={2} {...register("phones", { required: false })} />
        </div>
      </div>

      <div className={`${classes.row}
            ${errors.address ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>adresa</label>
          <input
            type="text"
            className={classes.itemBorder}
            {...register("address", { required: false })}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>napomena</label>
          <textarea rows={3} placeholder="" {...register("note")} />
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.buttons}>
          <button className={classes.iconButton} type="submit">
            <FontAwesomeIcon icon={faLongArrowAltDown} className={classes.icon} />
          </button>
        </div>
      </div>
    </form>
  );
};

export default NewPartnerPopover;
